import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {TextObservable} from "../../../stores/observables/textObservable";
import {useStores} from "../../../stores/useStores";
import classNames from "classnames";
import SearchSelection from "../../organisms/searchSelection/searchSelection";
import {GenericObservableList} from "../../../stores/observables/genericObservableList";
import {useTranslation} from "react-i18next";
import {runInAction} from "mobx";
import {EventBookingStatus} from "../../../shared/enums/eventBookingStatus";
import ResidentBookingGroup from "../../../stores/domainStore/pages/models/residentBookingGroup";
import {EventBookingSource} from "../../../shared/enums/eventBookingSource";
import EventBooking from "../../../stores/domainStore/pages/models/eventBooking";
import AreaTag from "../../atoms/areaTag/areaTag";

export interface IEventBookingSearchDropDown {
  /**
   * Resident name that the user adds in the search bar.
   */
  searchTerm: TextObservable;

  /**
   * Placeholder for search box
   */
  placeholder?: string;

  /**
   * To create a booking with this resident group for this event.
   */
  rowClickHandler: (resident: ResidentBookingGroup) => void;

  onBlur?: () => void;

  type: EventBookingStatus;
}

const EventBookingSearchDropDown: React.FC<IEventBookingSearchDropDown> = ({
  searchTerm,
  placeholder,
  rowClickHandler,
  onBlur,
  type
}) => {
  const {
    domainStore: {eventBookingResidentsDomainStore, eventsDomainStore},
    uiStore: {eventsUiStore}
  } = useStores();

  const {t} = useTranslation(["event"]);
  const [searchResults, setSearchResults] = useState<
    GenericObservableList<ResidentBookingGroup>
  >(new GenericObservableList<ResidentBookingGroup>([]));

  useEffect(() => {
    const allBookings = eventsUiStore.allBookings;

    // Create an indexed list to ensure no duplicate entries
    // The key for this list is the main residents platform id
    // which we know is unique for all bookings on this event
    const results = eventBookingResidentsDomainStore.residents.reduce(
      (results, r) => {
        // Find this residents booking (if it exists)
        const residentBooking = allBookings.find(
          (b) => b.personId === r.platformUserId
        );

        if (!residentBooking) {
          // There are no bookings for this resident, they are not part of a booking group
          results.set(
            r.platformUserId,
            ResidentBookingGroup.fromResidentDetail(r)
          );
          return results;
        }

        // Find all bookings created by the residents booking creator (not necessarily this resident)
        // Ensure you also filter by source, or else you might group a whole-bunch of staff bookings together
        const createdBookings = allBookings.filter(
          (b: EventBooking) =>
            b.creatorId === residentBooking.creatorId &&
            b.source === EventBookingSource.App
        );

        if (createdBookings.length > 1) {
          handlePlusOneBookings(createdBookings, results);
          return results;
        }

        // This resident is not part of a +1 booking, but does have a booking, we should propagate source
        results.set(
          r.platformUserId,
          ResidentBookingGroup.withEventBooking(r, residentBooking)
        );

        return results;
      },
      new Map<string, ResidentBookingGroup>()
    );

    setSearchResults(new GenericObservableList(Array.from(results.values())));
  }, [eventBookingResidentsDomainStore.residents, eventsUiStore]);

  const formatOptionLabel = (value: ResidentBookingGroup) => {
    const calculatedClassNames = classNames("options", {
      "confirmed-booking": value.bookingStatus === EventBookingStatus.Confirmed,
      "waitlist-booking": value.bookingStatus === EventBookingStatus.Waitlist,
      "cancelled-booking": value.bookingStatus === EventBookingStatus.Cancelled,
      "disable-resident": calculateDisableResident(value)
    });

    const calculatedStatus = (
      <>
        {value.bookingStatus === EventBookingStatus.Confirmed && <div>C</div>}
        {value.bookingStatus === EventBookingStatus.Waitlist && <div>W</div>}
        {value.bookingStatus === EventBookingStatus.Cancelled && <div>C</div>}
      </>
    );

    return (
      <div data-testid="search-result-row">
        <div className={calculatedClassNames}>
          <div>
            {value.isBookingOnBehalf && <span>+</span>}
            {value.mainResidentName}
            {calculatedStatus}
          </div>
          <div>
            <AreaTag personTypeId={value.personTypeId} />
          </div>
          <div>{value.unitCode}</div>
          <div>{value.mainResidentPhoneNumber}</div>
        </div>
      </div>
    );
  };

  const handlePlusOneBookings = (
    createdBookings: EventBooking[],
    results: Map<string, ResidentBookingGroup>
  ) => {
    // This resident is part of on behalf bookings
    const bookingGroups =
      ResidentBookingGroup.fromEventBookingGroup(createdBookings);
    if (bookingGroups) {
      bookingGroups.forEach((group) => {
        results.set(group.mainResidentPlatformId, group);
      });
    }
  };

  const calculateDisableResident = (option: ResidentBookingGroup) => {
    const event = eventsDomainStore?.editableEvent;
    const capacityRemaining: number =
      event?.bookingSpacesDetails?.capacityRemaining || 0;
    const isDisabledDueToCapacity =
      type === EventBookingStatus.Confirmed &&
      !event?.bookingSpacesDetails?.isUnlimitedCapacity &&
      capacityRemaining < option.requiredCapacity;

    const isConfirmedBooking =
      option.bookingStatus === EventBookingStatus.Confirmed;
    const isWaitlistBooking =
      type === EventBookingStatus.Waitlist &&
      option.bookingStatus === EventBookingStatus.Waitlist;

    return isDisabledDueToCapacity || isConfirmedBooking || isWaitlistBooking;
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={dropdownRef}>
      <SearchSelection
        items={searchResults}
        inputId="search-resident-input"
        formatOptionLabel={formatOptionLabel}
        optionDisable={(option) => calculateDisableResident(option)}
        onSearch={(value: string) => {
          searchTerm.setText(value);
          runInAction(() => {
            eventBookingResidentsDomainStore.isLoading = !!value;
            if (!value) {
              eventBookingResidentsDomainStore.hasNoSearchResult = false;
            }
          });
        }}
        onSelected={(resident: ResidentBookingGroup) => {
          rowClickHandler(resident);
          dropdownRef.current?.scrollIntoView();
        }}
        isLoading={eventBookingResidentsDomainStore.isLoading}
        onBlur={onBlur}
        placeholder={placeholder}
        noOptionsMessage={
          eventBookingResidentsDomainStore.isLoading
            ? ""
            : t("event:messages.noResidentsFound")
        }
        isOpenMenu={
          eventBookingResidentsDomainStore.isLoading ||
          eventBookingResidentsDomainStore.residents.length > 0 ||
          (eventBookingResidentsDomainStore.residents.length === 0 &&
            eventBookingResidentsDomainStore.hasNoSearchResult)
        }
      />
    </div>
  );
};

export default observer(EventBookingSearchDropDown);
